import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { Badge } from '@/shared/components/Badge'
import type { ColorDeBadge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { useState, useEffect } from 'react'
import { Search, Calendar, Building, Users, Eye, ExternalLink, Copy, MapPin, DollarSign, Clock } from 'lucide-react'
import { apiService } from '@/services/api'
import type { ApprovedJobRequest } from '@/services/api'
import { generateCandidateRegistrationLink, copyLinkToClipboard } from '@/features/rh/lib/candidateLink'
import { formatDateSafe, formatDate } from '@/shared/lib/dateUtils'
import CandidatesList from '@/features/rh/components/CandidatesList'
import ActiveJobDetail from '@/features/rh/components/ActiveJobDetail'

interface ActiveJobsListProps {
  onJobDetail?: (jobId: number) => void
}

const ActiveJobsList = ({ onJobDetail }: ActiveJobsListProps) => {
  const [activeJobs, setActiveJobs] = useState<ApprovedJobRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null)
  
  // Estado para la modal de candidatos
  const [selectedJobForCandidates, setSelectedJobForCandidates] = useState<{
    jobId: number
    jobTitle: string
  } | null>(null)
  
  // Estado para la vista de detalle
  const [selectedJobForDetail, setSelectedJobForDetail] = useState<number | null>(null)

  useEffect(() => {
    loadActiveJobs()
  }, [])

  const loadActiveJobs = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await apiService.getApprovedJobRequests()
      setActiveJobs(data || [])
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar vacantes activas:', err) // Debug log
      setError(err.message || 'Error al cargar las vacantes activas')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyPublicLink = async (e: React.MouseEvent, jobId: number) => {
    e.stopPropagation()
    const publicLink = await generateCandidateRegistrationLink(jobId)
    const success = await copyLinkToClipboard(publicLink)
    if (success) {
      setCopiedLinkId(jobId.toString())
      setTimeout(() => setCopiedLinkId(null), 2000)
    }
  }

  const handleViewCandidates = (e: React.MouseEvent, jobId: number, jobTitle: string) => {
    e.stopPropagation()
    setSelectedJobForCandidates({ jobId, jobTitle })
  }

  const handleCloseCandidatesModal = () => {
    setSelectedJobForCandidates(null)
  }

  const getUrgencyBadge = (prioridad: ApprovedJobRequest['prioridad']) => {
    const urgencyConfig = {
      baja: { color: 'gris' as ColorDeBadge, label: 'Baja' },
      media: { color: 'amarillo' as ColorDeBadge, label: 'Media' },
      alta: { color: 'naranja' as ColorDeBadge, label: 'Alta' },
      urgente: { color: 'rojo' as ColorDeBadge, label: 'Urgente' }
    }
    
    const config = (prioridad && urgencyConfig[prioridad]) || { color: 'gris' as ColorDeBadge, label: 'Sin urgencia' }
    return (
      <Badge color={config.color}>{config.label}</Badge>
    )
  }

  const formatSalary = (min: number | null, max: number | null) => {
    if (min && max && min > 0 && max > 0) {
      return `$${min.toLocaleString()} - $${max.toLocaleString()}`
    } else if (min && min > 0) {
      return `Desde $${min.toLocaleString()}`
    } else if (max && max > 0) {
      return `Hasta $${max.toLocaleString()}`
    }
    return 'Salario a convenir'
  }

  const filteredJobs = activeJobs.filter(job =>
    (job.puestoSolicitar && job.puestoSolicitar.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.areaVacante && job.areaVacante.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.justificacionNecesidad && job.justificacionNecesidad.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.solicitante?.firstName && job.solicitante.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.solicitante?.lastName && job.solicitante.lastName.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Cargando vacantes abiertas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <TituloDePantalla className="mb-2">
            Vacantes abiertas
          </TituloDePantalla>
          <p className="text-gray-600">
            Posiciones aprobadas donde los candidatos pueden registrarse
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="bg-blue-50 px-4 py-2 rounded-lg">
            <span className="text-blue-700 font-medium">
              {filteredJobs.length} vacante{filteredJobs.length !== 1 ? 's' : ''} activa{filteredJobs.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative max-w-md">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar vacantes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExternalLink className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Jobs Grid */}
      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-heading-sm text-secondary mb-2">
            {searchTerm ? 'No se encontraron vacantes' : 'No hay vacantes activas'}
          </h3>
          <p className="text-gray-600">
            {searchTerm 
              ? 'Intenta con otros términos de búsqueda.'
              : 'Aún no hay solicitudes aprobadas para mostrar como vacantes activas.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden"
            >
              {/* Header */}
              <div className="p-6 pb-4">
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {job.puestoSolicitar || 'Sin título'}
                  </h3>
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <Building className="w-4 h-4 mr-1" />
                    {job.areaVacante || 'Sin Departamento/Área'}
                  </div>
                </div>

                {/* Urgency and Candidate Count */}
                <div className="flex items-center justify-between mb-4">
                  {getUrgencyBadge(job.prioridad)}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <div className="flex items-center text-blue-700">
                      <Users className="w-5 h-5 mr-2" />
                      <div className="text-center">
                        <div className="text-2xl font-bold">{job.candidateCount || 0}</div>
                        <div className="text-xs uppercase tracking-wide font-medium">candidato{(job.candidateCount || 0) !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-700 line-clamp-3 mb-4">
                  {job.justificacionNecesidad || 'Sin descripción disponible'}
                </p>

                {/* Job Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                    {formatSalary(job.salarioMinimo, job.salarioMaximo)}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                    {job.numeroPlazas || 1} vacante{(job.numeroPlazas || 1) !== 1 ? 's' : ''}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2 text-purple-600" />
                    Inicio: {formatDateSafe(job.fechaRequeridaInicio, 'short')}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    <p><strong>Solicitante:</strong> {job.solicitante ? `${job.solicitante.firstName} ${job.solicitante.lastName}` : 'No especificado'}</p>
                    <p>Creada el {job.fechaCreacion ? formatDate(job.fechaCreacion) : 'Fecha no disponible'}</p>
                  </div>
                  <div className="flex items-center text-xs text-gray-400">
                    <Clock className="w-3 h-3 mr-1" />
                    Activa
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="px-6 py-4 space-y-3">
                {/* Fila de iconos con fondo suave */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={(e) => handleViewCandidates(e, job.id, job.puestoSolicitar)}
                    className="flex items-center justify-center p-3 rounded-lg transition-colors group hover:opacity-80"
                    style={{ backgroundColor: '#f2f4fa' }}
                    title="Ver candidatos"
                  >
                    <Users className="w-6 h-6 text-blue-600" />
                  </button>
                  <button
                    onClick={(e) => handleCopyPublicLink(e, job.id)}
                    className="flex items-center justify-center p-3 rounded-lg transition-colors group hover:opacity-80"
                    style={{ backgroundColor: '#f2f4fa' }}
                    title="Compartir link"
                  >
                    {copiedLinkId === job.id.toString() ? (
                      <Copy className="w-6 h-6 text-green-600" />
                    ) : (
                      <ExternalLink className="w-6 h-6 text-blue-600" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (onJobDetail) {
                        onJobDetail(job.id)
                      } else {
                        setSelectedJobForDetail(job.id)
                      }
                    }}
                    className="flex items-center justify-center p-3 rounded-lg transition-colors group hover:opacity-80"
                    style={{ backgroundColor: '#f2f4fa' }}
                    title="Ver detalles"
                  >
                    <Eye className="w-6 h-6 text-blue-600" />
                  </button>
                </div>

                {/* Botón principal de obtener link */}
                <button
                  onClick={(e) => handleCopyPublicLink(e, job.id)}
                  className={estiloDeBoton('primario', 'w-full')}
                >
                  <ExternalLink className="w-4 h-4" />
                  Obtener link de candidatos
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Candidatos */}
      {selectedJobForCandidates && (
        <CandidatesList
          jobRequestId={selectedJobForCandidates.jobId}
          jobTitle={selectedJobForCandidates.jobTitle}
          isOpen={true}
          onClose={handleCloseCandidatesModal}
        />
      )}

      {/* Modal de Detalle de Vacante */}
      {selectedJobForDetail && (
        <ActiveJobDetail
          jobId={selectedJobForDetail}
          onClose={() => setSelectedJobForDetail(null)}
        />
      )}
    </div>
  )
}

export default ActiveJobsList
